/// <amd-module name="Repos/CommitsList/Components/HistoryGraphRowGroup" />
import { GitCommitRef } from "Repos/Common/Contracts/Git";
import { ObservableValue } from "VSS/Core/Observable";
import {
    ExcisionVisComponents,
    HighlightDirection,
    IHistoryGraph,
    IHistoryGraphRow,
    VisComponents,
    VisualizationCell
} from "./HistoryGraphCell.Props";
import { ExcisionVisComponentsHelper, VisComponentsHelper } from "./HistoryGraphHelper";

export class HistoryGraphRowGroup implements IHistoryGraphRow {
    public id: number;
    public commit: GitCommitRef;
    public commitCellId: number;
    public maxCellId: number;
    public cells: {
        [key: number]: VisualizationCell;
    };
    public hasOutgoingExcisedCommits: boolean;
    public hasIncomingExcisedCommits: boolean;
    public aboveCommitId: string;
    public belowCommitId: string;
    public parentCommitIds: string[];
    public childCommitIds: string[];
    public parentGraph: IHistoryGraph;

    public get hasOngoingExcisedCommitTrackingLine(): boolean {
        return !!(
            this.parentGraph &&
            this.parentGraph.selectedCommitId.value &&
            !this.isSelected &&
            this.parentGraph.selectedOldestExcisedParentRowId !== this.id &&
            this.parentGraph.selectedYoungestExcisedChildRowId !== this.id &&
            this.isInSelectedRange()
        );
    }

    public get isSelected(): boolean {
        return !!(this.parentGraph && this.parentGraph.selectedCommitId.value === this.commit.commitId);
    }

    public get isSelectedExcisedCommitParent(): boolean {
        return !!(
            this.parentGraph &&
            this.parentGraph.selectedCommitId.value &&
            ((this.parentGraph.selectedExcisedParentCommitIds &&
                this.parentGraph.selectedExcisedParentCommitIds.indexOf(this.commit.commitId) >= 0) ||
                (this.isSelected && this.hasIncomingExcisedCommits))
        );
    }

    public get isSelectedExcisedCommitChild(): boolean {
        return !!(
            this.parentGraph &&
            this.parentGraph.selectedCommitId.value &&
            ((this.parentGraph.selectedExcisedChildCommitIds && this.parentGraph.selectedExcisedChildCommitIds.indexOf(this.commit.commitId) >= 0) ||
                (this.isSelected && this.hasOutgoingExcisedCommits))
        );
    }

    constructor(options: {
        id: number;
        commit: GitCommitRef;
        commitCellId: number;
        maxCellId: number;
        cells: {
            [key: number]: VisualizationCell;
        };
        hasOutgoingExcisedCommits: boolean;
        hasIncomingExcisedCommits: boolean;
        aboveCommitId: string;
        belowCommitId: string;
    }) {
        this.id = options.id;
        this.commit = options.commit;
        this.commitCellId = options.commitCellId;
        this.maxCellId = options.maxCellId;
        this.cells = options.cells;
        this.hasOutgoingExcisedCommits = options.hasOutgoingExcisedCommits;
        this.hasIncomingExcisedCommits = options.hasIncomingExcisedCommits;
        this.aboveCommitId = options.aboveCommitId;
        this.belowCommitId = options.belowCommitId;
    }

    /**
     * Constructs and returns the cell corresponding to the excision lane of the row.
     * It will primarily hold the excision components of the row
     */
    public getExcisionCell(): VisualizationCell {
        const components: VisComponents = VisComponents.None;
        let excisionComponents: ExcisionVisComponents = ExcisionVisComponents.None;

        if (this.hasOutgoingExcisedCommits || this.hasIncomingExcisedCommits) {
            excisionComponents |=
                this.hasOutgoingExcisedCommits && !this.isSelectedExcisedCommitChild
                    ? ExcisionVisComponents.OutgoingExcision
                    : ExcisionVisComponents.None;
            excisionComponents |=
                this.hasIncomingExcisedCommits && !this.isSelectedExcisedCommitParent
                    ? ExcisionVisComponents.IncomingExcision
                    : ExcisionVisComponents.None;
            excisionComponents |=
                this.hasOutgoingExcisedCommits && this.isSelectedExcisedCommitChild
                    ? ExcisionVisComponents.ExcisionHorizontal
                    : ExcisionVisComponents.None;
            excisionComponents |=
                this.hasIncomingExcisedCommits && this.isSelectedExcisedCommitParent
                    ? ExcisionVisComponents.ExcisionHorizontal
                    : ExcisionVisComponents.None;
        }
        // Id assigned to excision cell is -1
        const excisionCell: VisualizationCell = {
            components: components,
            excisionComponents: excisionComponents,
            highlitComponents: VisComponents.None,
            highlitExcisionComponents: ExcisionVisComponents.None,
            id: -1
        };
        let highlightExcisions = false;

        for (let i = 0; i <= this.maxCellId; i++) {
            const cell: VisualizationCell = this.cells[i];
            if (cell) {
                highlightExcisions = this.hasHighlitComponent(cell, VisComponents.LeftCenter);
                break;
            }
        }

        // Do not highlight if the excision is not selected
        if (this.isSelectedExcisedCommitChild || this.isSelectedExcisedCommitParent || highlightExcisions) {
            this.setExcisionHighlights(excisionCell, excisionComponents);
        }

        return excisionCell;
    }

    /**
     * Constructs and returns the cell corresponding to the tracing lane of the row.
     * It will primarily hold the excision components/ tracing line of the row
     */
    public getTracingCell(): VisualizationCell {
        const components: VisComponents = VisComponents.None;
        let excisionComponents: ExcisionVisComponents = ExcisionVisComponents.None;

        if (
            this.hasOutgoingExcisedCommits ||
            this.hasIncomingExcisedCommits ||
            this.hasOngoingExcisedCommitTrackingLine ||
            this.isSelectedExcisedCommitParent ||
            this.isSelectedExcisedCommitChild
        ) {
            excisionComponents |= this.hasOngoingExcisedCommitTrackingLine
                ? ExcisionVisComponents.ContinuingSelectedTrackingLine
                : ExcisionVisComponents.None;
            excisionComponents |=
                this.hasOutgoingExcisedCommits && this.isSelectedExcisedCommitChild
                    ? ExcisionVisComponents.OutgoingSelectedExcision
                    : ExcisionVisComponents.None;
            excisionComponents |=
                this.hasIncomingExcisedCommits && this.isSelectedExcisedCommitParent
                    ? ExcisionVisComponents.IncomingSelectedExcision
                    : ExcisionVisComponents.None;
        }

        // Id assigned to tracing cell is -2
        const tracingCell: VisualizationCell = {
            components: components,
            excisionComponents: excisionComponents,
            highlitComponents: VisComponents.None,
            highlitExcisionComponents: ExcisionVisComponents.None,
            id: -2
        };

        // We can always highlight all tracing cell since this cell is made visible only if the commit is either selectedParent/selectedChild/HasOngoingExcisedCommitTrackingLine
        this.highlightAll(tracingCell);

        return tracingCell;
    }

    /**
     * Highlight the current row based on the cells from the row below
     * This does not highlight the excision/tracing cells
     * @param cellsBelow - cells from the row below
     */
    public highlightFromBelow(cellsBelow: { [key: number]: VisualizationCell }): void {
        if (!this.cells || Object.keys(this.cells).length === 0 || !cellsBelow) {
            return;
        }

        const cellsAboveWithHorizontal: { [key: number]: boolean } = {};

        for (const cellId in cellsBelow) {
            const cellBelow: VisualizationCell = cellsBelow[cellId];
            const index: number = cellBelow.id;

            // Only if the cell below is highlighted, we would have any component to highlight in the current row
            if (this.hasHighlights(cellBelow)) {
                const upLeftCell: VisualizationCell = this.cells[index - 1];
                const upCenterCell: VisualizationCell = this.cells[index];
                const upRightCell: VisualizationCell = this.cells[index + 1];

                /*                 (0)   (1)   (2)
                 *               +++++++++++++++++++
                 *               +     +     +     +                      0- upLeftCell
                 *               +     +     +     +                      1- upCenterCell
                 *               +     +     +     +                      2- upRightCell
                 *               +     +     +     +
                 *               +++++++++++++++++++
                 *                     +     +
                 *                     +     +
                 *                     +     +
                 *                     +     +
                 *                     +++++++
                 *                    cellBelow
                 */

                const topHalf: VisComponents = VisComponents.TopLeft | VisComponents.TopMiddle | VisComponents.TopRight | VisComponents.Circle;
                if (
                    upLeftCell &&
                    this.hasHighlitComponent(cellBelow, VisComponents.TopLeft) &&
                    VisComponentsHelper.HasComponent(upLeftCell.components, VisComponents.BottomRight)
                ) {
                    /*
                     * 'upLeftCell' will get highlights only if 'cellBelow' has a top left highlight and 'upLeftCell' has bottom right
                     *
                     *                 (0)   (1)   (2)
                     *               +++++++++++++++++++
                     *               +     +     +     +                      0- upLeftCell
                     *               +     +     +     +                      1- upCenterCell
                     *               +   \ +     +     +                      2- upRightCell
                     *               +    \+     +     +
                     *               ++++++\++++++++++++
                     *                     +\    +
                     *                     + \   +
                     *                     +     +
                     *                     +     +
                     *                     +++++++
                     *                    cellBelow
                     */
                    this.setHighlights(upLeftCell, VisComponents.BottomRight | topHalf);
                    if (this.setHighlights(upLeftCell, VisComponents.RightCenter) && !cellsAboveWithHorizontal[index - 1]) {
                        cellsAboveWithHorizontal[index - 1] = true;
                    }
                }
                if (
                    upCenterCell &&
                    this.hasHighlitComponent(cellBelow, VisComponents.TopMiddle) &&
                    VisComponentsHelper.HasComponent(upCenterCell.components, VisComponents.OctopusMerge | VisComponents.BottomMiddle)
                ) {
                    /*
                     * 'upCenterCell' will get highlights only if 'cellBelow' has a top middle highlight and 'upCenterCell' has bottom middle
                     *
                     *                 (0)   (1)   (2)
                     *               +++++++++++++++++++
                     *               +     +     +     +                      0- upLeftCell
                     *               +     +     +     +                      1- upCenterCell
                     *               +     +  |  +     +                      2- upRightCell
                     *               +     +  |  +     +
                     *               +++++++++|+++++++++
                     *                     +  |  +
                     *                     +  |  +
                     *                     +     +
                     *                     +     +
                     *                     +++++++
                     *                    cellBelow
                     */
                    this.setHighlights(upCenterCell, VisComponents.BottomMiddle | VisComponents.OctopusMerge | topHalf);
                    if (this.setHighlights(upCenterCell, VisComponents.LeftMerge | VisComponents.RightMerge) && !cellsAboveWithHorizontal[index]) {
                        cellsAboveWithHorizontal[index] = true;
                    }
                }
                if (
                    upRightCell &&
                    this.hasHighlitComponent(cellBelow, VisComponents.TopRight) &&
                    VisComponentsHelper.HasComponent(upRightCell.components, VisComponents.BottomLeft)
                ) {
                    /*
                     * 'upRightCell' will get highlights only if 'cellBelow' has a top right highlight and 'upRightCell' has bottom left
                     *
                     *                 (0)   (1)   (2)
                     *               +++++++++++++++++++
                     *               +     +     +     +                      0- upLeftCell
                     *               +     +     +     +                      1- upCenterCell
                     *               +     +     + /   +                      2- upRightCell
                     *               +     +     +/    +
                     *               ++++++++++++/++++++
                     *                     +    /+
                     *                     +   / +
                     *                     +     +
                     *                     +     +
                     *                     +++++++
                     *                    cellBelow
                     */
                    this.setHighlights(upRightCell, VisComponents.BottomLeft | topHalf);
                    if (this.setHighlights(upRightCell, VisComponents.LeftCenter) && !cellsAboveWithHorizontal[index + 1]) {
                        cellsAboveWithHorizontal[index + 1] = true;
                    }
                }
            }
        }

        for (const cellIdString in cellsAboveWithHorizontal) {
            const cellId = parseInt(cellIdString);
            if (!isNaN(cellId)) {
                this.highlightHorizontal(cellId, HighlightDirection.FromBelow, false, false);
            }
        }
    }

    /**
     * Highlight the current row based on the cells from the row above
     * This does not highlight the excision/tracing cells
     * @param cellsAbove - cells from the row above
     */
    public highlightFromAbove(cellsAbove: { [key: number]: VisualizationCell }): void {
        if (!this.cells || Object.keys(this.cells).length === 0 || !cellsAbove) {
            return;
        }

        const cellsBelowWithHorizontal: { [key: number]: boolean } = {};

        for (const cellId in cellsAbove) {
            const cellAbove: VisualizationCell = cellsAbove[cellId];
            const index: number = cellAbove.id;

            // Only if the cell above is highlighted, we would have any component to highlight in the current row
            if (this.hasHighlights(cellAbove)) {
                const downLeftCell: VisualizationCell = this.cells[index - 1];
                const downCenterCell: VisualizationCell = this.cells[index];
                const downRightCell: VisualizationCell = this.cells[index + 1];

                /*                    cellAbove
                 *                     +++++++
                 *                     +     +
                 *                     +     +
                 *                     +     +
                 *                     +     +
                 *               +++++++++++++++++++
                 *               +     +     +     +                      0- downLeftCell
                 *               +     +     +     +                      1- downCenterCell
                 *               +     +     +     +                      2- downRightCell
                 *               +     +     +     +
                 *               +++++++++++++++++++
                 *                 (0)   (1)   (2)
                 */

                const bottomHalf: VisComponents =
                    VisComponents.BottomLeft | VisComponents.BottomMiddle | VisComponents.BottomRight | VisComponents.Circle;
                if (
                    downLeftCell &&
                    VisComponentsHelper.HasComponent(downLeftCell.components, VisComponents.TopRight) &&
                    this.hasHighlitComponent(cellAbove, VisComponents.BottomLeft)
                ) {
                    /*
                     * 'downLeftCell' will get highlights only if 'cellAbove' has a bottom left highlight and 'downLeftCell' has top right
                     *
                     *                    cellAbove
                     *                     +++++++
                     *                     +     +
                     *                     +     +
                     *                     + /   +
                     *                     +/    +
                     *               ++++++/++++++++++++
                     *               +    /+     +     +                      0- downLeftCell
                     *               +   / +     +     +                      1- downCenterCell
                     *               +     +     +     +                      2- downRightCell
                     *               +     +     +     +
                     *               +++++++++++++++++++
                     *                 (0)   (1)   (2)
                     */
                    this.setHighlights(downLeftCell, VisComponents.TopRight | bottomHalf);
                    if (
                        VisComponentsHelper.HasComponent(downLeftCell.components, VisComponents.Circle) &&
                        this.setHighlights(downLeftCell, VisComponents.RightCenter) &&
                        !cellsBelowWithHorizontal[index - 1]
                    ) {
                        cellsBelowWithHorizontal[index - 1] = true;
                    }
                }

                if (
                    downCenterCell &&
                    VisComponentsHelper.HasComponent(downCenterCell.components, VisComponents.TopMiddle) &&
                    this.hasHighlitComponent(cellAbove, VisComponents.BottomMiddle | VisComponents.OctopusMerge)
                ) {
                    /*
                     * 'downCenterCell' will get highlights only if 'cellAbove' has a bottom middle highlight and 'downLeftCell' has top middle
                     *
                     *                    cellAbove
                     *                     +++++++
                     *                     +     +
                     *                     +     +
                     *                     +  |  +
                     *                     +  |  +
                     *               +++++++++|+++++++++
                     *               +     +  |  +     +                      0- downLeftCell
                     *               +     +  |  +     +                      1- downCenterCell
                     *               +     +     +     +                      2- downRightCell
                     *               +     +     +     +
                     *               +++++++++++++++++++
                     *                 (0)   (1)   (2)
                     */
                    this.setHighlights(downCenterCell, VisComponents.TopMiddle | bottomHalf);
                    if (
                        VisComponentsHelper.HasComponent(downCenterCell.components, VisComponents.Circle) &&
                        this.setHighlights(downCenterCell, VisComponents.LeftCenter | VisComponents.RightCenter) &&
                        !cellsBelowWithHorizontal[index]
                    ) {
                        cellsBelowWithHorizontal[index] = true;
                    }
                }

                if (
                    downRightCell &&
                    VisComponentsHelper.HasComponent(downRightCell.components, VisComponents.TopLeft) &&
                    this.hasHighlitComponent(cellAbove, VisComponents.BottomRight)
                ) {
                    /*
                     * 'downRightCell' will get highlights only if 'cellAbove' has a bottom right highlight and 'downLeftCell' has top left
                     *
                     *                    cellAbove
                     *                     +++++++
                     *                     +     +
                     *                     +     +
                     *                     +   \ +
                     *                     +    \+
                     *               ++++++++++++\++++++
                     *               +     +     +\    +                      0- downLeftCell
                     *               +     +     + \   +                      1- downCenterCell
                     *               +     +     +     +                      2- downRightCell
                     *               +     +     +     +
                     *               +++++++++++++++++++
                     *                 (0)   (1)   (2)
                     */
                    this.setHighlights(downRightCell, VisComponents.TopLeft | bottomHalf);
                    if (
                        VisComponentsHelper.HasComponent(downRightCell.components, VisComponents.Circle) &&
                        this.setHighlights(downRightCell, VisComponents.LeftCenter) &&
                        !cellsBelowWithHorizontal[index + 1]
                    ) {
                        cellsBelowWithHorizontal[index + 1] = true;
                    }
                }
            }
        }

        for (const cellIdString in cellsBelowWithHorizontal) {
            const cellId = parseInt(cellIdString);
            if (!isNaN(cellId)) {
                this.highlightHorizontal(cellId, HighlightDirection.FromAbove, false, false);
            }
        }
    }

    /**
     * Highlighting the cells in the current row horizontally based on the highlight directions provided
     * @param commitCellId - cell Id from which the horizontal highlight begins
     * @param highlightDirection - the direction in which the current row is highlighted
     * @param highlightCentreAndAbove - dictates if the part of the cells above the center have to be highlighted
     * @param highlightCentreAndBelow - dictates if the part of the cells below the center have to be highlighted
     */
    public highlightHorizontal(
        commitCellId: number,
        highlightDirection: HighlightDirection,
        highlightCentreAndAbove: boolean,
        highlightCentreAndBelow: boolean
    ): void {
        let lastCell: VisualizationCell | null = null;
        let workingCell: VisualizationCell | null = null;
        const startingCell: VisualizationCell | null = this.cells[commitCellId];
        let i: number = commitCellId - 1;

        if (startingCell) {
            // Highlighting the cell which we are starting with
            workingCell = startingCell;
            lastCell = workingCell;
            if (highlightDirection === HighlightDirection.SelectionRow) {
                if (highlightCentreAndAbove) {
                    this.setHighlights(
                        workingCell,
                        VisComponents.TopLeft |
                            VisComponents.TopRight |
                            VisComponents.TopMiddle |
                            VisComponents.LeftCenter |
                            VisComponents.RightCenter
                    );
                }
                if (highlightCentreAndBelow) {
                    this.setHighlights(
                        workingCell,
                        VisComponents.BottomLeft |
                            VisComponents.BottomRight |
                            VisComponents.BottomMiddle |
                            VisComponents.LeftCenter |
                            VisComponents.RightCenter
                    );
                }
            } else if (highlightDirection === HighlightDirection.FromAbove) {
                this.setHighlights(workingCell, VisComponents.BottomLeft | VisComponents.BottomRight | VisComponents.BottomMiddle);
            } else if (highlightDirection === HighlightDirection.FromBelow) {
                this.setHighlights(workingCell, VisComponents.TopLeft | VisComponents.TopRight | VisComponents.TopMiddle);
            }

            // Highlighting the cells to the left of the start cell
            workingCell = this.cells[i];
            while (lastCell && this.hasHighlitComponent(lastCell, VisComponents.LeftCenter | VisComponents.LeftMerge) && workingCell && i >= 0) {
                i--;
                let addedHighlight = false;
                if (this.setHighlights(workingCell, VisComponents.LeftCenter) || this.setHighlights(workingCell, VisComponents.RightCenter)) {
                    addedHighlight = true;
                    this.setHighlights(workingCell, VisComponents.RightCenter);
                    if (highlightDirection === HighlightDirection.FromAbove) {
                        if (VisComponentsHelper.HasComponent(workingCell.components, VisComponents.Circle)) {
                            // Only if a commit is present, we must highlight the bottom components. Other bottom components cells are just intersection lines from other branches
                            this.setHighlights(workingCell, VisComponents.BottomMiddle | VisComponents.BottomLeft | VisComponents.BottomRight);
                        }
                    }
                    if (highlightDirection === HighlightDirection.FromBelow) {
                        if (VisComponentsHelper.HasComponent(workingCell.components, VisComponents.Circle)) {
                            // Only if a commit is present, we must highlight the top components. Other top components cells are just intersection lines from other branches
                            this.setHighlights(workingCell, VisComponents.TopMiddle | VisComponents.TopLeft | VisComponents.TopRight);
                        }
                    }
                }
                if (
                    (highlightDirection === HighlightDirection.FromAbove || highlightDirection === HighlightDirection.SelectionRow) &&
                    this.setHighlights(workingCell, VisComponents.RightMerge)
                ) {
                    addedHighlight = true;
                    this.setHighlights(workingCell, VisComponents.OctopusMerge);
                }
                if (!addedHighlight) {
                    break;
                }
                lastCell = workingCell;
                workingCell = this.cells[i];
            }

            // Highlighting the cells to the right of the start cell
            i = commitCellId + 1;
            lastCell = startingCell;
            workingCell = this.cells[i];
            while (lastCell && this.hasHighlitComponent(lastCell, VisComponents.RightCenter | VisComponents.RightMerge) && workingCell) {
                i++;
                let addedHighlight = false;
                if (this.setHighlights(workingCell, VisComponents.RightCenter) || this.setHighlights(workingCell, VisComponents.LeftCenter)) {
                    addedHighlight = true;
                    this.setHighlights(workingCell, VisComponents.LeftCenter);
                    if (highlightDirection === HighlightDirection.FromAbove) {
                        if (VisComponentsHelper.HasComponent(workingCell.components, VisComponents.Circle)) {
                            // Only if a commit is present, we must highlight the bottom components. Other bottom components cells are just intersection lines from other branches
                            this.setHighlights(workingCell, VisComponents.BottomMiddle | VisComponents.BottomLeft | VisComponents.BottomRight);
                        }
                    }
                    if (highlightDirection === HighlightDirection.FromBelow) {
                        if (VisComponentsHelper.HasComponent(workingCell.components, VisComponents.Circle)) {
                            // Only if a commit is present, we must highlight the top components. Other top components cells are just intersection lines from other branches
                            this.setHighlights(workingCell, VisComponents.TopMiddle | VisComponents.TopLeft | VisComponents.TopRight);
                        }
                    }
                }
                if (
                    (highlightDirection === HighlightDirection.FromAbove || highlightDirection === HighlightDirection.SelectionRow) &&
                    this.setHighlights(workingCell, VisComponents.LeftMerge)
                ) {
                    addedHighlight = true;
                    this.setHighlights(workingCell, VisComponents.OctopusMerge);
                }
                if (!addedHighlight) {
                    break;
                }
                lastCell = workingCell;
                workingCell = this.cells[i];
            }
        }
    }

    /**
     * Un-highlights all the cells in the current row
     */
    public unHighlight(): void {
        for (const key in this.cells) {
            this.unsetAllHighlights(this.cells[key]);
        }
    }

    /**
     * Highlights all the components and excision components in the cell
     */
    public highlightAll(cell: VisualizationCell): void {
        cell.highlitComponents = cell.components;
        cell.highlitExcisionComponents = cell.excisionComponents;
    }

    /**
     * Checks if the current row is present in the range of the selection.i.e. in between the youngest selected child commit and the oldest selected parent commit
     */
    private isInSelectedRange(): boolean {
        return !!(
            this.parentGraph &&
            this.parentGraph.selectedOldestExcisedParentRowId &&
            this.parentGraph.selectedOldestExcisedParentRowId >= 0 &&
            this.parentGraph.selectedYoungestExcisedChildRowId &&
            this.parentGraph.selectedYoungestExcisedChildRowId >= 0 &&
            this.id < this.parentGraph.selectedOldestExcisedParentRowId &&
            this.id > this.parentGraph.selectedYoungestExcisedChildRowId
        );
    }

    /**
     * Sets the given highlights to the components of the cell, if the corresponding VisComponents is present in the cell
     * @param components
     */
    private setHighlights(cell: VisualizationCell, components: VisComponents): boolean {
        let changed = false;
        VisComponentsHelper.ToList(cell.components).forEach((comp: VisComponents) => {
            changed = (comp & components) != VisComponents.None || changed;
            cell.highlitComponents |= comp & components;
        });
        return changed;
    }

    /**
     * Sets the given highlights to the excision components of the cell, if the corresponding ExcisionVisComponents is present in the cell
     * @param excisionComponents
     */
    private setExcisionHighlights(cell: VisualizationCell, excisionComponents: ExcisionVisComponents): boolean {
        let changed = false;
        ExcisionVisComponentsHelper.ToList(cell.excisionComponents).forEach((comp: ExcisionVisComponents) => {
            changed = (comp & excisionComponents) != ExcisionVisComponents.None || changed;
            cell.highlitExcisionComponents |= comp & excisionComponents;
        });
        return changed;
    }

    /**
     * Unsets highlight on all the components and excision components in the cell
     */
    private unsetAllHighlights(cell: VisualizationCell): void {
        cell.highlitComponents = VisComponents.None;
        cell.highlitExcisionComponents = ExcisionVisComponents.None;
    }

    private hasHighlights(cell: VisualizationCell): boolean {
        return cell.highlitComponents != VisComponents.None || cell.highlitExcisionComponents != ExcisionVisComponents.None;
    }

    /**
     * Checks if any VisComponents of the cell is highlit
     * @param query
     */
    private hasHighlitComponent(cell: VisualizationCell, query: VisComponents): boolean {
        return VisComponentsHelper.HasComponent(cell.highlitComponents, query);
    }

    /**
     * Checks if any ExcisionVisComponents of the cell is highlit
     * @param query
     */
    private hasHighlitExcisionComponent(cell: VisualizationCell, query: ExcisionVisComponents): boolean {
        return ExcisionVisComponentsHelper.HasComponent(cell.highlitExcisionComponents, query);
    }
}
